import { zodResolver } from '@hookform/resolvers/zod'
import { type ForgotPasswordRequest, ForgotPasswordRequestSchema } from '@gestao/shared'
import { Button, FormField, Input } from '@gestao/ui'
import { MailCheck } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { Link, useLocation } from 'react-router'
import type { z } from 'zod'

import { usePageTitle } from '../../app/use-page-title'
import { FormAlert } from '../../components/form-alert'
import { authLinkClasses } from './auth-link'
import * as t from '../../i18n/pt-BR'
import { errorMessage, fieldErrors } from '../../lib/api'
import { canAutoFocus } from '../../lib/auto-focus'
import { authApi } from './api'

type ForgotInput = z.input<typeof ForgotPasswordRequestSchema>

/** Pré-preenche com o e-mail digitado no login, quando veio de lá. */
function initialEmail(state: unknown): string {
  const email = (state as { email?: unknown } | null)?.email
  return typeof email === 'string' ? email : ''
}

/**
 * /esqueci-senha. A API sempre responde 200 (não revela se o e-mail tem conta), então a
 * confirmação fala em "se tiver acesso".
 */
export function ForgotPasswordPage() {
  usePageTitle(t.auth.forgot.title)
  const location = useLocation()
  const [sentTo, setSentTo] = useState<string | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const sentHeadingRef = useRef<HTMLHeadingElement>(null)
  const {
    register,
    handleSubmit,
    setError,
    setFocus,
    formState: { errors, isSubmitting },
  } = useForm<ForgotInput, unknown, ForgotPasswordRequest>({
    resolver: zodResolver(ForgotPasswordRequestSchema),
    defaultValues: { email: initialEmail(location.state) },
  })

  useEffect(() => {
    if (sentTo) sentHeadingRef.current?.focus()
    else if (canAutoFocus()) setFocus('email')
  }, [sentTo, setFocus])

  const onSubmit = handleSubmit(async (values) => {
    setFormError(null)
    try {
      await authApi.forgotPassword(values)
      setSentTo(values.email)
    } catch (error) {
      const message = fieldErrors(error).email
      if (message) setError('email', { message }, { shouldFocus: true })
      else setFormError(errorMessage(error))
    }
  })

  if (sentTo) {
    return (
      <div className="flex flex-col gap-6">
        <div
          aria-hidden
          className="grid size-12 place-items-center rounded-lg bg-petroleo-soft text-petroleo [&_svg]:size-6"
        >
          <MailCheck />
        </div>
        <div className="flex flex-col gap-2">
          <h1 ref={sentHeadingRef} tabIndex={-1} className="text-page text-ink outline-none">
            {t.auth.forgot.sentTitle}
          </h1>
          <p className="text-body text-ink-2">{t.auth.forgot.sentDescription(sentTo)}</p>
          <p className="text-body text-ink-2">{t.auth.forgot.sentHint}</p>
        </div>
        <div className="flex flex-col gap-4">
          <Button asChild className="h-10 w-full">
            <Link to="/login">{t.auth.forgot.back}</Link>
          </Button>
          <button type="button" onClick={() => setSentTo(null)} className={authLinkClasses}>
            {t.auth.forgot.sendAgain}
          </button>
        </div>
      </div>
    )
  }

  return (
    <form noValidate onSubmit={onSubmit} className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-page text-ink">{t.auth.forgot.title}</h1>
        <p className="text-body text-ink-2">{t.auth.forgot.description}</p>
      </div>

      <FormField label={t.auth.email} error={errors.email?.message}>
        <Input
          type="email"
          inputMode="email"
          autoComplete="username"
          autoCapitalize="none"
          spellCheck={false}
          className="h-10"
          {...register('email')}
        />
      </FormField>

      {formError ? <FormAlert>{formError}</FormAlert> : null}

      <div className="flex flex-col gap-4">
        <Button type="submit" loading={isSubmitting} className="h-10 w-full">
          {t.auth.forgot.submit}
        </Button>
        <Link to="/login" className={authLinkClasses}>
          {t.auth.forgot.back}
        </Link>
      </div>
    </form>
  )
}
